const parseProductJsonResponse = async (response, fallbackMessage = 'Product could not be added to cart.') => {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch (_error) {
    throw new Error(fallbackMessage);
  }
};

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.addEventListener('submit', (event) => {
          if (event.target.closest('product-form') === this) this.onSubmitHandler(event);
        });
        this.addEventListener('click', (event) => {
          const button = event.target.closest?.('[data-skip-cart-button="true"]');
          if (!button || button.closest('product-form') !== this) return;
          event.preventDefault();
          this.onDirectCheckout(button);
        });
      }

      connectedCallback() {
        this.refreshFormElements();
        queueMicrotask(() => { if (this.isConnected) this.refreshFormElements(); });
      }

      refreshFormElements() {
        const form = this.querySelector('form');
        const button = this.querySelector('[type="submit"]');
        if (this.initialized && this.form === form && this.submitButton === button) {
          if (this.variantIdInput) this.variantIdInput.disabled = false;
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          this.directCheckoutButtons = Array.from(this.querySelectorAll('[data-skip-cart-button="true"]'));
          this.actionButtons = [this.submitButton, ...this.directCheckoutButtons];
          return;
        }
        this.disconnectedCallback();
        this.form = form;
        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.form || !this.submitButton || !this.variantIdInput) return;

        this.variantIdInput.disabled = false;
        this.initialized = true;
        this.errorMessageWrapper = null;
        this.errorMessage = null;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButtonText = this.submitButton.querySelector('.main-atc__label__text') || this.submitButton.querySelector('span');
        this.directCheckoutRequested = false;
        this.directCheckoutButton = null;
        this.activeActionButton = null;
        this.directCheckoutButtons = Array.from(this.querySelectorAll('[data-skip-cart-button="true"]'));
        this.actionButtons = [this.submitButton, ...this.directCheckoutButtons];
        this.acceleratedCheckoutUpsells = this.querySelector('[data-accelerated-checkout-upsells]');
        this.productInfo = this.closest('product-info');

        if (this.acceleratedCheckoutUpsells && this.productInfo && !this.closest('product-info-upsell')) {
          this.onUpsellChange = () => this.syncAcceleratedCheckoutUpsells();
          this.onUpsellInputChange = (event) => {
            if (
              event.target !== this.variantIdInput
              && !event.target.closest('product-info-upsell, quantity-break-upsells, quantity-gifts')
            ) return;
            queueMicrotask(() => this.syncAcceleratedCheckoutUpsells());
          };
          this.productInfo.addEventListener('upsell:change', this.onUpsellChange);
          this.productInfo.addEventListener('quantity-gifts:change', this.onUpsellChange);
          this.productInfo.addEventListener('quantity-break:change', this.onUpsellChange);
          this.productInfo.addEventListener('change', this.onUpsellInputChange);
          this.acceleratedCheckoutObserver = new MutationObserver((mutations) => {
            const addedUpsell = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) =>
              node.nodeType === Node.ELEMENT_NODE
                && (
                  node.matches?.('product-info-upsell, quantity-break-upsells, quantity-gifts')
                  || node.querySelector?.('product-info-upsell, quantity-break-upsells, quantity-gifts')
                )
            ));
            if (addedUpsell) this.syncAcceleratedCheckoutUpsells();
          });
          this.acceleratedCheckoutObserver.observe(this.productInfo, { childList: true, subtree: true });
          this.syncAcceleratedCheckoutUpsells();
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        this.refreshFormElements();
        const traceId = window.CartDebug?.start(this, evt.submitter || this.submitButton);
        const log = (stage, data = {}) => window.CartDebug?.log(stage, { traceId, ...data });
        if (!this.form || !this.submitButton || !this.variantIdInput) {
          log('submit.blocked', { reason: 'missing-form-or-button' });
          return;
        }
        const actionButton = this.directCheckoutRequested && this.directCheckoutButton
          ? this.directCheckoutButton
          : evt.submitter || this.submitButton;
        if (this.submitting || actionButton.disabled || actionButton.getAttribute('aria-disabled') === 'true') {
          log('submit.blocked', { reason: this.submitting ? 'already-submitting' : 'button-disabled' });
          return;
        }
        if (!this.closest('product-info-upsell, cart-drawer-upsell') &&
            this.productInfo?.querySelector('subscription-widget')?.dataset.variantPending === 'true') {
          log('submit.blocked', { reason: 'subscription-pending' });
          this.handleErrorMessage('Please wait for the selected purchase options to finish updating.', true);
          return;
        }
        // A lost response is not proof that Shopify did not add the items.
        // Reviewing the cart must precede another purchase attempt in this form.
        if (this.purchaseNeedsReview) {
          window.location.assign(this.getRecoveryCartUrl());
          return;
        }

        let requestStarted = false;
        let addConfirmed = false;
        let linesUpdateDeferred = null;
        let slowTimer;
        this.submitting = true;
        this.activeActionButton = actionButton;
        try {
          if (!this.validateCustomProductFields()) { log('submit.blocked', { reason: 'custom-field-invalid' }); return; }
          this.error = false;
          this.handleErrorMessage(false, true);
          this.setActionButtonsBusy(actionButton, true);

          const shouldSkipCart = this.directCheckoutRequested || this.variantIdInput?.dataset.skipCart === 'true';
          if (shouldSkipCart) {
            await this.withPurchaseDeadline(() => this.prepareDirectCheckout());
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          this.syncSelectedVariantInput();
          const formData = new FormData(this.form);
          this.removeAcceleratedCheckoutFields(formData);
          this.appendSelectedAdditionalItems(formData);
          const bundleVariantIds = formData.getAll('items[][id]').filter(Boolean);
          if (bundleVariantIds.length) {
            formData.delete('id');
            formData.delete('quantity');
          }
          if (this.cart?.getSectionsToRender) {
            formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement?.(document.activeElement);
          }
          const cartRequest = this.buildMultiItemCartRequest(formData, bundleVariantIds);
          if (cartRequest) {
            config.headers['Content-Type'] = 'application/json';
            config.body = JSON.stringify(cartRequest);
          } else {
            config.body = formData;
          }

          const variantId = formData.get('id') || bundleVariantIds[0] || '';
          const quantity = parseInt(formData.get('quantity')) || 1;
          linesUpdateDeferred = bundleVariantIds.length ? null : this.createCartLinesUpdateEvent(variantId, quantity);
          log('prepare.ready', { variantId, quantity, skipCart: shouldSkipCart });
          const drawer = document.querySelector('cart-drawer');
          log('queue.wait', { pending: Boolean(drawer?.cartMutationQueue) });
          slowTimer = window.setTimeout(() => {
            log('submit.slow', { phase: requestStarted ? 'request' : 'queue' });
            this.showPurchaseRecovery('Your cart update is still pending. Please wait or check your cart before trying again.');
          }, 10000);
          const send = () => {
            requestStarted = true;
            log('request.start');
            return this.requestPurchase(config, traceId);
          };
          const response = await (drawer?.enqueueCartMutation ? drawer.enqueueCartMutation(send) : send());
          window.clearTimeout(slowTimer);
          this.handleErrorMessage(false, true);
          if (response.status) {
            log('request.rejected', { status: response.status });
            if (shouldSkipCart && Number(response.status) === 422 &&
                await this.withPurchaseDeadline(() => this.canCheckoutExistingCart(formData, bundleVariantIds))) {
              addConfirmed = true;
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              this.renderDirectCheckoutCart(response);
              await this.withPurchaseDeadline(() => this.prepareDirectCheckout());
              this.submitGuestCheckout();
              return;
            }
            const message = this.getPurchaseErrorMessage(response);
            this.showPurchaseRecovery(message);
            this.notifyPurchaseError(message, 'INVALID', linesUpdateDeferred, variantId);
            // Availability belongs to the variant picker, not to every API error.
            return;
          }

          addConfirmed = true;
          log('request.confirmed');
          this.resolveCartLinesUpdate(linesUpdateDeferred);
          if (shouldSkipCart) {
            this.renderDirectCheckoutCart(response);
            await this.withPurchaseDeadline(() => this.prepareDirectCheckout());
            this.submitGuestCheckout();
            return;
          }
          if (!this.cart) {
            window.location.assign(this.getRecoveryCartUrl());
            return;
          }
          try {
            Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form', productVariantId: variantId, cartData: response,
            })).catch((eventError) => console.error(eventError));
          } catch (eventError) {
            console.error(eventError);
          }
          this.purchaseTraceId = traceId;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => this.renderPurchaseCart(response));
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.renderPurchaseCart(response);
          }
        } catch (error) {
          console.error(error);
          log('submit.error', { requestStarted, addConfirmed, code: error.code || error.name });
          this.purchaseNeedsReview = requestStarted;
          const message = addConfirmed
            ? 'Your items were added, but the next step could not be opened. View your cart to continue.'
            : requestStarted
              ? 'We could not confirm whether your items were added. Please check your cart before trying again.'
              : 'Your items could not be submitted. Please try again. If this continues, refresh the page.';
          this.showPurchaseRecovery(message);
          // A display/navigation error must not reject an already successful add.
          if (!addConfirmed) this.notifyPurchaseError(message, 'SERVICE_UNAVAILABLE', linesUpdateDeferred);
        } finally {
          window.clearTimeout(slowTimer);
          this.directCheckoutRequested = false;
          this.directCheckoutButton = null;
          this.activeActionButton = null;
          this.submitting = false;
          this.setActionButtonsBusy(actionButton, false);
          log('submit.finish', { requestStarted, addConfirmed, locked: this.submitting });
        }
      }

      getRecoveryCartUrl() {
        return window.routes?.cart_url || (typeof routes !== 'undefined' && routes.cart_url) || '/cart';
      }

      async withPurchaseDeadline(task, controller) {
        let timer;
        try {
          return await Promise.race([
            Promise.resolve().then(task),
            new Promise((_, reject) => {
              timer = setTimeout(() => {
                reject(Object.assign(new Error('Purchase request timed out'), { code: 'TIMEOUT' }));
                controller?.abort();
              }, 15000);
            }),
          ]);
        } finally {
          clearTimeout(timer);
        }
      }

      requestPurchase(config, traceId) {
        const controller = new AbortController();
        return this.withPurchaseDeadline(async () => {
          const httpResponse = await fetch(`${routes.cart_add_url}`, { ...config, signal: controller.signal });
          window.CartDebug?.log('http.response', { traceId, status: httpResponse.status });
          const payload = await parseProductJsonResponse(httpResponse);
          if (httpResponse.status >= 500) throw new Error('Purchase service unavailable');
          if (!payload || typeof payload !== 'object' || Array.isArray(payload)) throw new Error('Invalid purchase response');
          if (!httpResponse.ok && !payload.status) payload.status = httpResponse.status || 'ERROR';
          // Valid JSON alone does not establish a successful cart mutation.
          if (!payload.status && !payload.id && !(Array.isArray(payload.items) && payload.items.length && payload.items.every((item) => item?.id))) {
            throw new Error('Missing added items in purchase response');
          }
          return payload;
        }, controller);
      }

      getPurchaseErrorMessage(response) {
        const message = response.description || response.message;
        if (typeof message === 'string' && message.trim() && !/<[^>]+>|Unexpected token|SyntaxError|TypeError|ReferenceError|Failed to fetch|DOCTYPE/i.test(message)) {
          return message;
        }
        return 'These items could not be added. Please check your selection and try again.';
      }

      notifyPurchaseError(message, code, deferred, variantId = '') {
        try {
          Promise.resolve(publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form', productVariantId: variantId, errors: message, message,
          })).catch((error) => console.error(error));
          this.dispatchCartErrorEvent(message, code);
        } catch (error) {
          console.error(error);
        }
        deferred?.reject(new Error(message));
      }

      renderPurchaseCart(response) {
        try {
          if (!response.sections || this.cart.getSectionsToRender?.().some((section) => !response.sections[section.id])) {
            throw new Error('Cart section was not rendered');
          }
          this.measure('add:paint-updated-sections', () => this.cart.renderContents(response));
          window.CartDebug?.log('render.complete', { traceId: this.purchaseTraceId });
        } catch (error) {
          console.error(error);
          this.purchaseNeedsReview = true;
          this.showPurchaseRecovery('Your items were added, but the cart could not be displayed. View your cart to continue.');
          // Quick-add forms are hidden once the modal closes: expose the recovery
          // on the cart page, without repeating the add request.
          if (this.closest('quick-add-modal')) {
            window.location.assign(this.getRecoveryCartUrl());
          }
        }
      }

      renderDirectCheckoutCart(response) {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer?.renderContents || !response?.sections?.['cart-drawer']) return;
        drawer.renderContents(response, { open: false, reconcile: false });
      }

      showPurchaseRecovery(message) {
        this.handleErrorMessage(message, true);
        if (!this.errorMessage) return;
        const link = document.createElement('a');
        link.href = this.getRecoveryCartUrl();
        link.className = 'link underlined-link';
        link.textContent = 'View cart';
        link.dataset.purchaseRecovery = 'true';
        this.errorMessage.append(' ', link);
      }

      onDirectCheckout(button) {
        this.refreshFormElements();
        if (!this.form || !this.submitButton) return;
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
        this.directCheckoutRequested = true;
        this.directCheckoutButton = button;
        this.form.requestSubmit(this.submitButton);
      }

      handleSubmit(event) {
        this.refreshFormElements();
        const button = event?.currentTarget || event?.target;
        event?.preventDefault?.();
        if (button?.matches?.('[data-skip-cart-button="true"]')) {
          this.onDirectCheckout(button);
          return;
        }
        this.form?.requestSubmit?.(this.submitButton);
      }

      async prepareDirectCheckout() {
        if (this.closest('product-info-upsell, cart-drawer-upsell')) return;
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;
        await drawer.flushUpsellIntents?.();
        await drawer.waitForCartMutations?.();
        for (const gift of drawer.querySelectorAll('cart-drawer-gift[data-id]')) {
          const result = await gift.update?.();
          if (result?.error) throw result.error;
        }
        if (drawer.querySelector('cart-drawer-upsell[data-toggle="true"][data-id]')) {
          if (typeof drawer.syncPreselectedUpsells !== 'function') throw new Error('Cart add-ons are not ready');
          const result = await drawer.syncPreselectedUpsells();
          if (result?.error) throw result.error;
          if (!Array.isArray(result?.cart?.items)) throw new Error('Cart add-ons could not be verified');
        }
        await drawer.waitForCartMutations?.();
      }

      submitGuestCheckout() {
        const cartUrl = window.routes?.cart_url || (typeof routes !== 'undefined' ? routes.cart_url : '/cart');
        const checkoutForm = document.createElement('form');
        checkoutForm.method = 'post';
        checkoutForm.action = cartUrl;
        checkoutForm.hidden = true;

        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = 'Checkout';
        checkoutForm.appendChild(checkoutInput);
        document.body.appendChild(checkoutForm);
        checkoutForm.submit();
      }

      syncSelectedVariantInput() {
        const submittedVariantId = this.form.dataset.submitVariantId;
        if (submittedVariantId && this.variantIdInput) {
          this.variantIdInput.value = submittedVariantId;
          delete this.form.dataset.submitVariantId;
          return;
        }

        const productInfo = this.closest('product-info');
        const variantPicker = productInfo?.querySelector('variant-selects');
        const selectedVariant = variantPicker?.getSelectedVariant?.();
        if (!selectedVariant?.id || !this.variantIdInput) return;

        this.variantIdInput.value = String(selectedVariant.id);
      }

      syncAcceleratedCheckoutUpsells() {
        if (!this.acceleratedCheckoutUpsells || !this.productInfo) return;

        this.syncSelectedVariantInput();
        this.acceleratedCheckoutUpsells.replaceChildren();
        const bundleVariantIds = this.getSelectedBundleVariantIds();
        const mainVariantId = String(bundleVariantIds[0] || this.variantIdInput?.value || '');
        if (mainVariantId && this.variantIdInput) this.variantIdInput.value = mainVariantId;
        const selectedUpsells = this.getSelectedAdditionalItems();
        let itemIndex = 1;

        bundleVariantIds.slice(1).forEach((variantId) => {
          this.appendAcceleratedCheckoutInput(itemIndex, 'id', variantId);
          this.appendAcceleratedCheckoutInput(itemIndex, 'quantity', '1');
          itemIndex += 1;
        });

        selectedUpsells.forEach((upsell) => {
          const variantId = String(upsell.id || '');
          if (!variantId) return;

          this.appendAcceleratedCheckoutInput(itemIndex, 'id', variantId);
          this.appendAcceleratedCheckoutInput(itemIndex, 'quantity', upsell.quantity || '1');
          if (mainVariantId) this.appendAcceleratedCheckoutInput(itemIndex, 'parent_id', mainVariantId);
          if (upsell.sellingPlan) this.appendAcceleratedCheckoutInput(itemIndex, 'selling_plan', upsell.sellingPlan);
          Object.entries(upsell.properties || {}).forEach(([name, value]) => {
            if (value !== '') this.appendAcceleratedCheckoutInput(itemIndex, `properties][${name}`, value);
          });
          itemIndex += 1;
        });
      }

      getSelectedBundleVariantIds() {
        if (!this.form) return [];
        return Array.from(this.form.querySelectorAll('.product-form__variants input[name="items[][id]"]'))
          .map((input) => String(input.value || ''))
          .filter(Boolean);
      }

      appendAcceleratedCheckoutInput(itemIndex, attribute, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `shopify-additional-items[${itemIndex}][${attribute}]`;
        input.value = String(value);
        this.acceleratedCheckoutUpsells.appendChild(input);
      }

      removeAcceleratedCheckoutFields(formData) {
        Array.from(formData.keys()).forEach((name) => {
          if (name.startsWith('shopify-additional-items[')) formData.delete(name);
        });
      }

      buildMultiItemCartRequest(formData, bundleVariantIds) {
        if (!bundleVariantIds.length) return null;

        const items = [];
        let currentItem = null;
        const mainProperties = {};

        formData.forEach((value, name) => {
          if (name === 'items[][id]') {
            currentItem = { id: Number(value), quantity: 1 };
            items.push(currentItem);
            return;
          }
          if (name === 'items[][quantity]' && currentItem) {
            currentItem.quantity = Math.max(1, parseInt(value, 10) || 1);
            return;
          }
          if (name === 'items[][selling_plan]' && currentItem && value) {
            currentItem.selling_plan = Number(value);
            return;
          }
          if (name === 'items[][parent_id]' && currentItem && value) {
            currentItem.parent_id = Number(value);
            return;
          }
          const itemPropertyMatch = name.match(/^items\[\]\[properties\]\[(.+)\]$/);
          if (itemPropertyMatch && currentItem && value !== '') {
            currentItem.properties ||= {};
            currentItem.properties[itemPropertyMatch[1]] = String(value);
            return;
          }

          const propertyMatch = name.match(/^properties\[(.+)\]$/);
          if (propertyMatch && value !== '') mainProperties[propertyMatch[1]] = String(value);
        });

        if (!items.length) return null;
        const mainSellingPlan = formData.get('selling_plan');
        items.filter((item) => !item.parent_id).forEach((item) => {
          if (Object.keys(mainProperties).length) item.properties = { ...mainProperties, ...(item.properties || {}) };
          if (mainSellingPlan && !item.selling_plan) item.selling_plan = Number(mainSellingPlan);
        });

        const request = { items };
        const sections = formData.get('sections');
        const sectionsUrl = formData.get('sections_url');
        if (sections) request.sections = String(sections);
        if (sectionsUrl) request.sections_url = String(sectionsUrl);
        return request;
      }

      getSelectedAdditionalItems() {
        if (!this.productInfo) return [];
        const items = [];
        this.productInfo.querySelectorAll('product-info-upsell[data-selected="true"][data-id]').forEach((upsell) => {
          if (upsell.dataset.variantAvailable === 'false') return;
          items.push({
            id: upsell.dataset.id,
            quantity: '1',
            key: upsell.dataset.upsellKey || `${upsell.dataset.productId || ''}-${upsell.dataset.id}`,
            properties: {
              _theme_upsell_key: upsell.dataset.upsellKey || `${upsell.dataset.productId || ''}-${upsell.dataset.id}`,
            },
            sellingPlan: upsell.querySelector('input[name="selling_plan"]:not(:disabled)')?.value || '',
          });
        });
        this.productInfo
          .querySelectorAll('quantity-break-upsells:not([hidden]) [data-additional-key][data-product][data-selected="true"]')
          .forEach((item) => {
            items.push({
              id: item.dataset.product,
              quantity: '1',
              key: item.dataset.additionalKey,
              properties: {
                _theme_additional_key: item.dataset.additionalKey,
                _theme_parent_product_id: this.productInfo.dataset.productId || '',
                _theme_min_parent_quantity: item.dataset.minQuantity || '1',
                _theme_additional_kind: item.dataset.type || 'upsell',
              },
              sellingPlan: item.querySelector('input[name="selling_plan"]:not(:disabled)')?.value || '',
            });
          });
        this.productInfo
          .querySelectorAll('quantity-gifts .quantity-gift[data-additional-key][data-product][data-unlocked="true"]')
          .forEach((gift) => {
            items.push({
              id: gift.dataset.product,
              quantity: '1',
              key: gift.dataset.additionalKey,
              properties: {
                _theme_additional_key: gift.dataset.additionalKey,
                _theme_parent_product_id: this.productInfo.dataset.productId || '',
                _theme_min_parent_quantity: gift.dataset.quantity || '1',
                _theme_additional_kind: 'gift',
              },
              sellingPlan: gift.querySelector('input[name="selling_plan"]:not(:disabled)')?.value || '',
            });
          });
        const seen = new Set();
        return items.filter((item) => {
          const identity = `${Object.keys(item.properties || {})[0] || ''}:${item.key}`;
          if (!item.id || !item.key || seen.has(identity)) return false;
          seen.add(identity);
          return true;
        });
      }

      setActionButtonsBusy(activeButton, busy) {
        this.actionButtons.forEach((button) => {
          if (busy) {
            if (button.dataset.productFormBusy !== 'true') {
              button.dataset.productFormBusy = 'true';
              button.dataset.productFormPreviousAria = button.hasAttribute('aria-disabled')
                ? button.getAttribute('aria-disabled')
                : '__missing__';
            }
            button.setAttribute('aria-disabled', 'true');
          } else if (button.dataset.productFormBusy === 'true') {
            const previousAria = button.dataset.productFormPreviousAria;
            if (previousAria === '__missing__') button.removeAttribute('aria-disabled');
            else button.setAttribute('aria-disabled', previousAria || 'false');
            delete button.dataset.productFormBusy;
            delete button.dataset.productFormPreviousAria;
          }
        });

        activeButton?.classList.toggle('loading', busy);
        activeButton
          ?.querySelector('.loading-overlay__spinner, .loading__spinner')
          ?.classList.toggle('hidden', !busy);
      }

      async canCheckoutExistingCart(formData, bundleVariantIds) {
        const fields = Array.from(formData.entries());
        const hasSellingPlan = fields.some(([name, value]) => name.includes('selling_plan') && value);
        const hasLineProperties = fields.some(([name, value]) => name.includes('properties[') && value);
        if (hasSellingPlan || hasLineProperties) return false;

        const variantIds = bundleVariantIds.length
          ? formData.getAll('items[][id]').filter(Boolean)
          : [formData.get('id')].filter(Boolean);
        const quantities = bundleVariantIds.length
          ? formData.getAll('items[][quantity]')
          : [formData.get('quantity')];
        if (!variantIds.length) return false;

        const requestedQuantities = new Map();
        variantIds.forEach((variantId, index) => {
          const id = String(variantId);
          const quantity = Math.max(1, parseInt(quantities[index], 10) || 1);
          requestedQuantities.set(id, (requestedQuantities.get(id) || 0) + quantity);
        });

        try {
          const cartPath = window.routes?.cart_url || (typeof routes !== 'undefined' ? routes.cart_url : '/cart');
          const response = await fetch(`${cartPath.replace(/\/$/, '')}.js`, {
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) return false;

          const cart = await response.json();
          const cartQuantities = new Map();
          (cart.items || []).forEach((item) => {
            const id = String(item.variant_id || item.id || '');
            if (!id) return;
            cartQuantities.set(id, (cartQuantities.get(id) || 0) + (parseInt(item.quantity, 10) || 0));
          });

          return Array.from(requestedQuantities.entries()).every(
            ([variantId, quantity]) => (cartQuantities.get(variantId) || 0) >= quantity
          );
        } catch (_error) {
          return false;
        }
      }

      validateCustomProductFields() {
        if (!this.productInfo) return true;
        const fields = Array.from(this.productInfo.querySelectorAll('custom-product-field'));
        const firstInvalid = fields.find((field) => field.validate?.(false) === false);
        if (!firstInvalid) return true;
        firstInvalid.validate?.(true);
        return false;
      }

      appendSelectedAdditionalItems(formData) {
        if (this.closest('product-info-upsell')) return;

        const selectedUpsells = this.getSelectedAdditionalItems();
        if (!selectedUpsells.length) return;

        const existingVariantIds = formData.getAll('items[][id]').filter(Boolean).map(String);
        let mainVariantId = existingVariantIds[0] || String(formData.get('id') || '');
        if (!existingVariantIds.length) {
          mainVariantId = String(formData.get('id') || '');
          if (!mainVariantId) return;

          const mainQuantity = formData.get('quantity') || '1';
          const mainSellingPlan = formData.get('selling_plan');
          formData.delete('id');
          formData.delete('quantity');
          formData.delete('selling_plan');
          formData.append('items[][id]', mainVariantId);
          formData.append('items[][quantity]', mainQuantity);
          if (mainSellingPlan) formData.append('items[][selling_plan]', mainSellingPlan);
        }

        selectedUpsells.forEach((upsell) => {
          formData.append('items[][id]', String(upsell.id));
          formData.append('items[][quantity]', String(upsell.quantity || '1'));
          if (mainVariantId) formData.append('items[][parent_id]', mainVariantId);
          if (upsell.sellingPlan) formData.append('items[][selling_plan]', upsell.sellingPlan);
          Object.entries(upsell.properties || {}).forEach(([name, value]) => {
            if (value !== '') formData.append(`items[][properties][${name}]`, value);
          });
        });
      }

      disconnectedCallback() {
        this.initialized = false;
        this.acceleratedCheckoutObserver?.disconnect();
        this.productInfo?.removeEventListener('upsell:change', this.onUpsellChange);
        this.productInfo?.removeEventListener('quantity-gifts:change', this.onUpsellChange);
        this.productInfo?.removeEventListener('quantity-break:change', this.onUpsellChange);
        this.productInfo?.removeEventListener('change', this.onUpsellInputChange);
      }

      handleErrorMessage(errorMessage = false, force = false) {
        if (this.hideErrors && !force) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper && force) {
          this.errorMessageWrapper = document.createElement('div');
          this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
          this.errorMessageWrapper.setAttribute('role', 'alert');
          const text = document.createElement('span');
          text.className = 'product-form__error-message';
          this.errorMessageWrapper.appendChild(text);
          this.form.prepend(this.errorMessageWrapper);
        }
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (this.errorMessage) this.errorMessage.textContent = errorMessage || ''; 
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = this.submitButton.dataset.customLabel || window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      measure(name, callback) {
        if (window.CartPerformance?.measure) window.CartPerformance.measure(name, callback);
        else callback();
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form?.querySelector('[name=id]');
      }

    }
  );
}
